import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { requestSignupOtp } from "../api/signup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../components/Spinner";
import { seoDefaults } from "../utils/seoDefaults";
import SEO from "../api/seo";

const Signup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_number: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const passwordsMatch = formData.password === formData.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;

    if (!emailRegex.test(formData.email)) {
      const msg = "Please enter a valid email address";
      setError(msg);
      toast.error(msg);
      return;
    }

    if (!phoneRegex.test(formData.phone_number)) {
      const msg = "Please enter a valid 10-digit phone number";
      setError(msg);
      toast.error(msg);
      return;
    }

    if (!passwordsMatch) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      const ipInfo = await fetch("https://ipapi.co/json/").then((res) =>
        res.json()
      );

      const payload = {
        name: formData.name,
        email: formData.email,
        phone_number: formData.phone_number,
        password: formData.password,
        ip: ipInfo.ip,
        ip_location: `${ipInfo.city}, ${ipInfo.region}`,
        timezone: ipInfo.timezone,
      };

      const res = await requestSignupOtp(payload);
      toast.success("OTP sent to your email");
      navigate("/signup/otp-verify", { state: { email: res.email } });
    } catch (err) {
      setError(err.response?.data?.message || "Signup failed");
      toast.error(err.response?.data?.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO {...seoDefaults.signup} />
      <div className="min-h-screen flex items-center justify-center bg-gray-950 text-white relative overflow-hidden">
        {/* Background blobs */}
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-cyan-900/15 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-purple-900/20 rounded-full filter blur-3xl animate-pulse"></div>
        {loading && <Spinner />}
        <div className="relative z-10 w-full max-w-md p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 shadow-lg border border-gray-700/60 my-32">
          <h2 className="text-3xl font-bold text-center mb-6 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
            Create Your Account
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm mb-1 text-gray-300">
                Full Name
              </label>
              <input
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Gamer Name"
                required
              />
            </div>

            <div>
              <label className="block text-sm mb-1 text-gray-300">Email</label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm mb-1 text-gray-300">
                Phone Number
              </label>
              <input
                name="phone_number"
                type="text"
                value={formData.phone_number}
                onChange={handleChange}
                maxLength={10}
                className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="+91 XXXXX XXXXX"
                required
              />
            </div>

            <div>
              <label className="block text-sm mb-1 text-gray-300">
                Password
              </label>
              <input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="********"
                required
              />
            </div>

            <div>
              <label className="block text-sm mb-1 text-gray-300">
                Confirm Password
              </label>
              <input
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full p-3 rounded-lg bg-gray-800 border ${
                  passwordsMatch ? "border-gray-700" : "border-red-500"
                } text-white placeholder-gray-500 focus:outline-none focus:ring-2 ${
                  passwordsMatch
                    ? "focus:ring-purple-500"
                    : "focus:ring-red-500"
                }`}
                placeholder="Re-enter password"
                required
              />
              {!passwordsMatch && (
                <p className="text-sm text-red-400 mt-1">
                  Passwords do not match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={!passwordsMatch}
              className={`w-full py-3 rounded-lg ${
                passwordsMatch
                  ? "bg-gradient-to-r from-cyan-500 to-purple-600"
                  : "bg-gray-700 cursor-not-allowed"
              } text-white font-semibold tracking-wide transition-all`}
            >
              SIGN UP
            </button>
            {error && (
              <p className="text-sm font-semibold text-red-400 mt-2 text-center">{error}</p>
            )}
          </form>

          <p className="mt-5 text-center text-sm text-gray-400">
            Already have an account?{" "}
            <Link to="/login" className="text-cyan-400 hover:underline">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default Signup;
